// 型システムの境界を1箇所へ集約するユーティリティ。
// as(型表明)と非null表明は原則禁止(ESLint)。どうしても必要な表明は本ファイルの
// 関数内に正当化コメント付きで閉じ込め、呼び出し側は表明なしで型安全に使う。

// Object.keys はキー型を意図的に string[] へ落とす(構造的部分型で余分なキーが
// 入り得るため)。キー集合が閉じているオブジェクト(レジストリ・Partial マップ・
// 生成された対応表)に限って、宣言どおりのキー型で返す。
// eslint-disable-next-line @typescript-eslint/ban-types -- ジェネリクスの「非プリミティブ」制約としての object は正当(値の型としては使わない)
export function objectKeys<T extends object>(target: T): (keyof T)[] {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.keys の戻り値は string[] 固定のため(閉じたキー集合専用)
  return Object.keys(target) as (keyof T)[];
}

// null / undefined でないことを実行時に検査して型を絞る(非null表明の代替)。
// 「直前の処理が保証しているはず」の前提を、失敗時に原因が分かる例外へ変える。
export function required<T>(value: T | null | undefined, label: string): T {
  if (value == null) {
    throw new Error(`${label} が存在しません(データ不整合)。`);
  }
  return value;
}

// オフラインフォールバック(IndexedDB DataApi)の結果を応答宣言型へ吸収する境界。
// 従来の request() はオフライン結果の null を応答型(非null宣言)のまま呼び出し側へ返しており、
// ページ側はこの実挙動を前提に動作している。正規化応答への移行でオフライン経路を
// Api メソッド内へ明示した後も、この従来契約(行が無ければ実行時に null が返る)を変えないための境界。
export function offlineResultBoundary<TRow>(row: TRow | null): TRow {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 従来の request() と同一の null 吸収境界(実行時挙動不変。ここ1箇所に集約)
  return row as TRow;
}

// 予期しない例外(unknown)からユーザー向けメッセージを取り出す(catch (e: any) の代替)。
export function errorMessageOf(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === "object" && e !== null && "message" in e) {
    return String(e.message);
  }
  return String(e);
}

// 動的なプロパティ名でオブジェクトから値を読む(動的キー駆動 UI 専用)。
// 戻り値は unknown なので、呼び出し側は typeof 等で絞ってから使う。
// eslint-disable-next-line @typescript-eslint/ban-types -- ジェネリクスの「非プリミティブ」制約としての object は正当(値の型としては使わない)
export function readProp(target: object, key: string | number | symbol): unknown {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 閉じた型で表せない動的キーの読み出し境界(書き込みには使わない)
  const record = target as Record<string | number | symbol, unknown>;
  return record[key];
}
